import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft,
  faShoppingCart,
  faCreditCard,
  faCheck,
  faSpinner,
  faExclamationTriangle,
  faUser,
  faEnvelope,
  faPhone,
  faMapMarkerAlt,
  faTrash,
  faMinus,
  faPlus
} from '@fortawesome/free-solid-svg-icons';
import Footer from '../../Footer/Footer';
import { theme } from '../../../styles/theme';
import { useBasket } from '../../../contexts/BasketContext';
import { woocommerceService } from '../../../services/woocommerceService';
// Legacy CustomStripeCheckout removed

const CheckoutContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: ${props => props.theme.colors.offWhite};
  padding-top: 200px;
  overflow-x: hidden;
  max-width: 100vw;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    padding-top: 160px;
  }
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  width: 100%;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    padding: 0 0.875rem;
  }
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: white;
  border: 2px solid ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.primary};
  padding: 0.8rem 1.5rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 2rem;
  
  &:hover {
    background: ${props => props.theme.colors.primary};
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(16, 29, 64, 0.2);
  }
`;

const CheckoutGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 400px);
  gap: 3rem;
  box-sizing: border-box;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const CheckoutForm = styled.div`
  background: white;
  border-radius: 8px;
  padding: 2rem;
  box-shadow: ${props => props.theme.shadows.light};
  border: 1px solid ${props => props.theme.colors.greyBackground};
  min-width: 0;
  box-sizing: border-box;
`;

const OrderSummary = styled.div`
  background: white;
  border-radius: 8px;
  padding: 2rem;
  box-shadow: ${props => props.theme.shadows.light};
  border: 1px solid ${props => props.theme.colors.greyBackground};
  height: fit-content;
  position: sticky;
  top: 220px;
  min-width: 0;
  box-sizing: border-box;
  
  @media (max-width: 1024px) {
    position: static;
  }
`;

const SectionTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.8rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  font-size: 0.9rem;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 0.5rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.8rem 1rem;
  border: 2px solid ${props => props.theme.colors.greyBackground};
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.3s ease;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.accent};
    box-shadow: 0 0 0 3px rgba(157, 109, 73, 0.1);
  }
  
  &.error {
    border-color: #F44336;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.8rem 1rem;
  border: 2px solid ${props => props.theme.colors.greyBackground};
  border-radius: 8px;
  font-size: 1rem;
  min-height: 100px;
  resize: vertical;
  transition: all 0.3s ease;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.accent};
    box-shadow: 0 0 0 3px rgba(157, 109, 73, 0.1);
  }
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 1rem;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const PaymentMethods = styled.div`
  margin-top: 2rem;
`;

const PaymentMethod = styled.div`
  border: 2px solid ${props => props.selected ? props.theme.colors.accent : props.theme.colors.greyBackground};
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  background: ${props => props.selected ? 'rgba(157, 109, 73, 0.05)' : 'white'};
  
  &:hover {
    border-color: ${props => props.theme.colors.accent};
  }
`;

const PaymentMethodHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  flex-wrap: wrap;
`;

const OrderItem = styled.div`
  display: flex;
  gap: 1rem;
  padding: 1rem 0;
  border-bottom: 1px solid ${props => props.theme.colors.greyBackground};
  
  &:last-child {
    border-bottom: none;
  }
`;

const ItemImage = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 8px;
  overflow: hidden;
  background: ${props => props.theme.colors.greyBackground};
  flex-shrink: 0;
`;

const ItemImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ItemDetails = styled.div`
  flex: 1;
  min-width: 0;
`;

const ItemName = styled.h4`
  font-size: 0.9rem;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 0.5rem 0;
  line-height: 1.3;
  word-break: break-word;
  hyphens: auto;
`;

const ItemPrice = styled.span`
  font-size: 0.9rem;
  font-weight: 600;
  color: ${props => props.theme.colors.accent};
`;

const ItemQuantity = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const QuantityButton = styled.button`
  width: 24px;
  height: 24px;
  border: 1px solid ${props => props.theme.colors.greyBackground};
  background: white;
  color: ${props => props.theme.colors.primary};
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.theme.colors.accent};
    color: white;
    border-color: ${props => props.theme.colors.accent};
  }
  
  &:disabled {
    background: #f0f0f0;
    color: #ccc;
    cursor: not-allowed;
  }
`;

const QuantityInput = styled.input`
  width: 40px;
  height: 24px;
  border: 1px solid ${props => props.theme.colors.greyBackground};
  text-align: center;
  font-size: 0.8rem;
  border-radius: 4px;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.accent};
  }
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: #F44336;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  transition: all 0.3s ease;
  
  &:hover {
    background: #F44336;
    color: white;
  }
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
  
  &.total {
    font-size: 1.1rem;
    font-weight: 700;
    color: ${props => props.theme.colors.primary};
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 2px solid ${props => props.theme.colors.greyBackground};
  }
`;

const CheckoutButton = styled.button`
  width: 100%;
  padding: 1.2rem 2rem;
  background: ${props => props.theme.colors.accent};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: ${props => props.theme.shadows.light};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 2rem;
  
  &:hover {
    background: ${props => props.theme.colors.primary};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(16, 29, 64, 0.2);
  }
  
  &:disabled {
    background: ${props => props.theme.colors.greyBackground};
    color: #999;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const EmptyCart = styled.div`
  text-align: center;
  padding: 3rem 2rem;
  color: ${props => props.theme.colors.primary};
`;

const EmptyIcon = styled.div`
  font-size: 3rem;
  color: ${props => props.theme.colors.greyBackground};
  margin-bottom: 1rem;
`;

const ErrorMessage = styled.div`
  background: #ffebee;
  color: #F44336;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const SuccessMessage = styled.div`
  background: #e8f5e8;
  color: #4CAF50;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Checkout = () => {
  const navigate = useNavigate();
  const { 
    items, 
    totalItems, 
    totalPrice, 
    updateQuantity, 
    removeFromBasket, 
    clearBasket 
  } = useBasket();

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postcode: '',
    country: 'United Kingdom',
    notes: ''
  });

  const [isProcessing, setIsProcessing] = useState(false);
  const [errors, setErrors] = useState({});
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [orderNumber, setOrderNumber] = useState(null);
  const [orderError, setOrderError] = useState(null);
  // Only Stripe is supported
  const [useStripe, setUseStripe] = useState(true);
  const [paymentIntent, setPaymentIntent] = useState(null);
  const [clientSecret, setClientSecret] = useState('');
  const [stripePromise] = useState(() => loadStripe(process.env.REACT_APP_STRIPE_PK || 'pk_test_51Q2WRjHK1jv4FVhH0sWrqHpkUjRqx3Q1MtQNWlHmTuCrjG6V2ynMDVzKHtKEeXrqpA9jZGQXbUJaGQXAzrqlJdEo009Yp1MaFa'));

  // Redirect if cart is empty
  useEffect(() => {
    if (totalItems === 0 && !orderSuccess) {
      navigate('/duchess-collection');
    }
  }, [totalItems, navigate, orderSuccess]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email is invalid';
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    if (!formData.address.trim()) newErrors.address = 'Address is required';
    if (!formData.city.trim()) newErrors.city = 'City is required';
    if (!formData.postcode.trim()) newErrors.postcode = 'Postcode is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleQuantityChange = (itemId, change) => {
    const item = items.find(item => item.id === itemId);
    if (item) {
      const newQuantity = item.quantity + change;
      if (newQuantity > 0) {
        updateQuantity(itemId, newQuantity);
      }
    }
  };

  const handleQuantityInput = (itemId, value) => {
    const quantity = parseInt(value);
    if (quantity > 0) {
      updateQuantity(itemId, quantity);
    }
  };

  const handleStripePaymentSuccess = async (paymentIntent) => {
    setPaymentIntent(paymentIntent);
    try {
      const order = await createWooCommerceOrder(true);
      // Redirect to success page with order details
      navigate(`/duchess-collection/checkout/success?order=${order.id}&payment_intent=${paymentIntent.id}&total=${order.total}`);
    } catch (error) {

      // Redirect to failure page with error details
      navigate(`/duchess-collection/checkout/failure?error_message=${encodeURIComponent(error.message)}`);
    }
  };

  const handleStripePaymentError = (error) => {
    setOrderError(error);
    // Redirect to failure page with error details
    navigate(`/duchess-collection/checkout/failure?error_message=${encodeURIComponent(error)}`);
  };

  // Redirect to WooCommerce checkout for Stripe payments
  const handleStripeCheckout = async () => {
    try {

      // Debug Store API first
      await woocommerceService.debugStoreApi();
      
      // On-site Stripe via WooCommerce Store API
      // 1) Sync cart to Store API
      const cartItems = items.map(it => {
        if (it.variation_id) {
          return {
            id: it.id,
            variation_id: it.variation_id,
            quantity: it.quantity,
            variation: it.variation || undefined
          };
        }
        return { id: it.id, quantity: it.quantity };
      });

      await woocommerceService.setCartItems(cartItems);

      // Sanity check
      const cart = await woocommerceService.getCart();

      if (!cart || !cart.items_count) {
        throw new Error('Cart is empty at checkout. Please re-add items.');
      }

      // 2) Create checkout to initialize Stripe (no redirect)
      const billing = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        address_1: formData.address,
        city: formData.city,
        postcode: formData.postcode,
        country: 'GB'
      };

      // Since Store API payment methods endpoint might be 404, let's try a different approach
      // We'll attempt checkout with different strategies
      
      let checkoutRes = null;
      let lastError = null;
      
      // Strategy: use Stripe directly
      checkoutRes = await woocommerceService.checkoutWithStripe({
        billing_address: billing,
        shipping_address: billing,
        payment_method: 'stripe',
        payment_data: {}
      });

      const cs = woocommerceService.extractStripeClientSecret(checkoutRes);
      if (!cs) {

        throw new Error('Unable to initialize payment: missing client secret. Please check Stripe configuration.');
      }

      setClientSecret(cs);
      
    } catch (error) {

      throw new Error(`Stripe checkout failed: ${error.message}`);
    }
  };

  const createWooCommerceOrder = async (isPaid = false) => {
    try {
      // Prepare order data for WooCommerce
      const orderData = {
        payment_method: 'stripe',
        payment_method_title: 'Stripe',
        set_paid: isPaid,
        billing: {
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          address_1: formData.address,
          city: formData.city,
          postcode: formData.postcode,
          country: 'GB'
        },
        shipping: {
          first_name: formData.firstName,
          last_name: formData.lastName,
          address_1: formData.address,
          city: formData.city,
          postcode: formData.postcode,
          country: 'GB'
        },
        line_items: items.map(item => ({
          product_id: item.id,
          quantity: item.quantity,
          name: item.name,
          price: item.price
        })),
        shipping_lines: [
          {
            method_id: 'flat_rate',
            method_title: 'Standard Shipping',
            total: shippingCost === 0 ? '0' : shippingCost.toString()
          }
        ],
        customer_note: formData.notes || '',
        status: isPaid ? 'processing' : 'pending',
        meta_data: paymentIntent ? [
          {
            key: '_stripe_payment_intent_id',
            value: paymentIntent.id
          }
        ] : []
      };

      // Create order in WooCommerce
      const order = await woocommerceService.createOrder(orderData);
      
      // Clear basket
      clearBasket();
      
      // Redirect to success page with order details
      const orderNumber = order.number || order.id;
      const shippingCost = totalPrice > 50 ? 0 : 5.99;
      const total = order.total || (totalPrice + shippingCost);
      navigate(`/duchess-collection/checkout/success?order=${orderNumber}&total=${total}`);
      
    } catch (error) {

      // Redirect to failure page with error details
      navigate(`/duchess-collection/checkout/failure?error_message=${encodeURIComponent(error.message || 'Failed to create order. Please try again.')}`);
    }
  };

  const handleCheckout = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsProcessing(true);
    setOrderError(null);
    
    try {
      if (useStripe) {
        // Initialize on-site Stripe flow (no redirect)

        await handleStripeCheckout();

      } else {
        // Traditional WooCommerce order creation

        await createWooCommerceOrder(false);
      }
    } catch (error) {

      let errorMessage = error.message || 'Failed to create order. Please try again.';
      
      // Provide more helpful error messages
      if (error.message.includes('payment method')) {
        errorMessage = 'Payment method configuration error. Please contact support or try again later.';
      } else if (error.message.includes('client secret')) {
        errorMessage = 'Payment initialization failed. Please check your connection and try again.';
      } else if (error.message.includes('Cart is empty')) {
        errorMessage = 'Your cart appears to be empty. Please add items and try again.';
      }
      
      setOrderError(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  if (orderSuccess) {
    return (
      <CheckoutContainer>
        <ContentContainer>
          <SuccessMessage>
            <FontAwesomeIcon icon={faCheck} />
            Order #{orderNumber} placed successfully! You will receive a confirmation email shortly. Redirecting to shop...
          </SuccessMessage>
        </ContentContainer>
        <Footer />
      </CheckoutContainer>
    );
  }

  const StripeInlinePay = () => {
    const stripe = useStripe();
    const elements = useElements();

    const onPay = async () => {
      if (!stripe || !elements) return;
      setIsProcessing(true);
      setOrderError(null);
      const { error: confirmError, paymentIntent: pi } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required'
      });
      if (confirmError) {
        setIsProcessing(false);
        setOrderError(confirmError.message || 'Payment failed');
        return;
      }
      // Success
      clearBasket();
      navigate(`/duchess-collection/checkout/success`);
    };

    return (
      <div style={{ marginTop: '1rem' }}>
        <PaymentElement />
        <CheckoutButton type="button" onClick={onPay} disabled={isProcessing || !stripe}>
          {isProcessing ? (
            <>
              <FontAwesomeIcon icon={faSpinner} spin />
              Processing...
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faCreditCard} />
              Pay Securely
            </>
          )}
        </CheckoutButton>
      </div>
    );
  };

  if (totalItems === 0) {
    return (
      <CheckoutContainer>
        <ContentContainer>
          <EmptyCart>
            <EmptyIcon>
              <FontAwesomeIcon icon={faShoppingCart} />
            </EmptyIcon>
            <h2>Your cart is empty</h2>
            <p>Add some items to your cart to proceed to checkout.</p>
          </EmptyCart>
        </ContentContainer>
        <Footer />
      </CheckoutContainer>
    );
  }

  const shippingCost = totalPrice > 50 ? 0 : 5.99;
  const finalTotal = totalPrice + shippingCost;

  return (
    <CheckoutContainer>
      <ContentContainer>
        <BackButton onClick={() => navigate('/duchess-collection')}>
          <FontAwesomeIcon icon={faArrowLeft} />
          Back to Collection
        </BackButton>

        <CheckoutGrid>
          <CheckoutForm>
            {orderError && (
              <ErrorMessage>
                <FontAwesomeIcon icon={faExclamationTriangle} />
                {orderError}
              </ErrorMessage>
            )}
            
            <form onSubmit={handleCheckout}>
              <SectionTitle>
                <FontAwesomeIcon icon={faUser} />
                Contact Information
              </SectionTitle>
              
              <Row>
                <FormGroup>
                  <Label>First Name *</Label>
                  <Input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className={errors.firstName ? 'error' : ''}
                    placeholder="Enter your first name"
                  />
                  {errors.firstName && <ErrorMessage>{errors.firstName}</ErrorMessage>}
                </FormGroup>
                
                <FormGroup>
                  <Label>Last Name *</Label>
                  <Input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className={errors.lastName ? 'error' : ''}
                    placeholder="Enter your last name"
                  />
                  {errors.lastName && <ErrorMessage>{errors.lastName}</ErrorMessage>}
                </FormGroup>
              </Row>

              <Row>
                <FormGroup>
                  <Label>Email Address *</Label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={errors.email ? 'error' : ''}
                    placeholder="Enter your email"
                  />
                  {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
                </FormGroup>
                
                <FormGroup>
                  <Label>Phone Number *</Label>
                  <Input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className={errors.phone ? 'error' : ''}
                    placeholder="Enter your phone number"
                  />
                  {errors.phone && <ErrorMessage>{errors.phone}</ErrorMessage>}
                </FormGroup>
              </Row>

              <SectionTitle>
                <FontAwesomeIcon icon={faMapMarkerAlt} />
                Shipping Address
              </SectionTitle>

              <FormGroup>
                <Label>Address *</Label>
                <Input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  className={errors.address ? 'error' : ''}
                  placeholder="Enter your address"
                />
                {errors.address && <ErrorMessage>{errors.address}</ErrorMessage>}
              </FormGroup>

              <Row>
                <FormGroup>
                  <Label>City *</Label>
                  <Input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    className={errors.city ? 'error' : ''}
                    placeholder="Enter your city"
                  />
                  {errors.city && <ErrorMessage>{errors.city}</ErrorMessage>}
                </FormGroup>
                
                <FormGroup>
                  <Label>Postcode *</Label>
                  <Input
                    type="text"
                    name="postcode"
                    value={formData.postcode}
                    onChange={handleInputChange}
                    className={errors.postcode ? 'error' : ''}
                    placeholder="Enter your postcode"
                  />
                  {errors.postcode && <ErrorMessage>{errors.postcode}</ErrorMessage>}
                </FormGroup>
              </Row>

              <FormGroup>
                <Label>Country</Label>
                <Input
                  type="text"
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  disabled
                />
              </FormGroup>

              <FormGroup>
                <Label>Order Notes (Optional)</Label>
                <TextArea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  placeholder="Any special instructions for your order..."
                />
              </FormGroup>

              <PaymentMethods>
                <SectionTitle>
                  <FontAwesomeIcon icon={faCreditCard} />
                  Payment Method
                </SectionTitle>

                <PaymentMethod selected>
                  <PaymentMethodHeader>
                    <FontAwesomeIcon icon={faCreditCard} />
                    Card
                  </PaymentMethodHeader>
                  <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.9rem', color: '#666' }}>
                    Secure payment with Apple Pay, Google Pay, or card via WooCommerce
                  </p>
                </PaymentMethod>
              </PaymentMethods>

              {/* Stripe on-site Payment Element */}
              {useStripe && clientSecret && stripePromise && (
                <Elements stripe={stripePromise} options={{ clientSecret }}>
                  <StripeInlinePay />
                </Elements>
              )}
            </form>
          </CheckoutForm>

          <OrderSummary>
            <SectionTitle>
              <FontAwesomeIcon icon={faShoppingCart} />
              Order Summary
            </SectionTitle>

            {items.map(item => (
              <OrderItem key={item.id}>
                <ItemImage>
                  <ItemImg
                    src={item.image}
                    alt={item.name}
                    onError={(e) => {
                      e.target.src = '/placeholder-product.jpg';
                    }}
                  />
                </ItemImage>
                
                <ItemDetails>
                  <ItemName>{item.name}</ItemName>
                  <ItemPrice>£{item.price.toFixed(2)}</ItemPrice>
                  
                  <ItemQuantity>
                    <QuantityButton
                      onClick={() => handleQuantityChange(item.id, -1)}
                      disabled={item.quantity <= 1}
                    >
                      <FontAwesomeIcon icon={faMinus} />
                    </QuantityButton>
                    
                    <QuantityInput
                      type="number"
                      value={item.quantity}
                      onChange={(e) => handleQuantityInput(item.id, e.target.value)}
                      min="1"
                      max="10"
                    />
                    
                    <QuantityButton
                      onClick={() => handleQuantityChange(item.id, 1)}
                      disabled={item.quantity >= 10}
                    >
                      <FontAwesomeIcon icon={faPlus} />
                    </QuantityButton>
                    
                    <RemoveButton
                      onClick={() => removeFromBasket(item.id)}
                      title="Remove item"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </RemoveButton>
                  </ItemQuantity>
                </ItemDetails>
              </OrderItem>
            ))}

            <SummaryRow>
              <span>Subtotal</span>
              <span>£{totalPrice.toFixed(2)}</span>
            </SummaryRow>
            
            <SummaryRow>
              <span>Shipping</span>
              <span>{shippingCost === 0 ? 'Free' : `£${shippingCost.toFixed(2)}`}</span>
            </SummaryRow>
            
            {shippingCost > 0 && (
              <SummaryRow style={{ fontSize: '0.8rem', color: '#666' }}>
                <span>Free shipping on orders over £50</span>
                <span>£{(50 - totalPrice).toFixed(2)} more needed</span>
              </SummaryRow>
            )}
            
            <SummaryRow className="total">
              <span>Total</span>
              <span>£{finalTotal.toFixed(2)}</span>
            </SummaryRow>

            {!clientSecret && (
              <CheckoutButton 
                type="submit" 
                onClick={handleCheckout}
                disabled={isProcessing}
              >
                {isProcessing ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin />
                    Processing...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faCreditCard} />
                    Pay
                  </>
                )}
              </CheckoutButton>
            )}
          </OrderSummary>
        </CheckoutGrid>
      </ContentContainer>
      
      <Footer />
    </CheckoutContainer>
  );
};

export default Checkout;